/**
 * Copyright © 2026 Adnuntius AS.
 */
import {DestroyRef, computed, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {catchError, defer, EMPTY, finalize, Subject, switchMap} from 'rxjs';

import {visiblePageNumbers} from '../common/directives/pagination/visible-page-numbers';
import {
  SiteApiService,
  SiteCreativeSummaryItem,
  SiteCreativeSummaryType
} from './site-api.service';

export class SiteCreativeSummaryState {
  readonly items = signal<SiteCreativeSummaryItem[]>([]);
  readonly totalCount = signal(0);
  readonly currentPage = signal(1);
  readonly loading = signal(true);
  readonly loadFailed = signal(false);

  readonly uniqueCount = computed(() => new Set(this.items().map((item) => item.hash)).size);
  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.totalCount() / this.pageSize)));
  readonly pageNumbers = computed(() => visiblePageNumbers(this.currentPage(), this.totalPages()));
  readonly pageRangeStart = computed(() => this.pageSize * (this.currentPage() - 1) + 1);
  readonly pageRangeEnd = computed(() => Math.min(this.pageSize * this.currentPage(), this.totalCount()));

  private readonly reload = new Subject<void>();

  constructor(
    private readonly api: SiteApiService,
    private readonly siteId: string,
    readonly summaryType: SiteCreativeSummaryType,
    readonly pageSize: number,
    private readonly locale: string | undefined,
    destroyRef: DestroyRef
  ) {
    this.reload.pipe(
      switchMap(() => defer(() => {
        this.loading.set(true);
        this.loadFailed.set(false);
        return this.api.getCreativeSummary(this.siteId, {
          summaryType: this.summaryType,
          page: this.currentPage(),
          pageSize: this.pageSize,
          locale: this.locale
        }).pipe(
          catchError(() => {
            this.loadFailed.set(true);
            return EMPTY;
          }),
          finalize(() => this.loading.set(false))
        );
      })),
      takeUntilDestroyed(destroyRef)
    ).subscribe((page) => {
      this.items.set(page.results || []);
      this.totalCount.set(page.totalCount || 0);
    });
  }

  load(): void {
    this.reload.next();
  }

  changePage(page: number, event: Event): void {
    event.preventDefault();
    if (this.loading() || page < 1 || page > this.totalPages() || page === this.currentPage()) {
      return;
    }
    this.currentPage.set(page);
    this.load();
  }
}
